import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PromptIntro from "../components/wizards/custom/PromptIntro";
import CustomBotWizard from "../components/wizards/custom/CustomBotWizard";
import MainLayout from "../components/layout/MainLayout";
import { useQueryClient } from "@tanstack/react-query";
import { billingKeys, queryKeys, useBillingUsage, useBots } from "../hooks/useApi";
import LimiteAssistenti from "../components/billing/LimiteAssistenti";

// La modalità scelta sopravvive al refresh (per scheda); il draft degli
// step vive in CustomBotWizard con la stessa strategia.
const MODE_STORAGE_KEY = "pingup-onboarding-mode";
// Testo e sito della entry prompt-first: sopravvivono al refresh (i file
// allegati no: gli oggetti File non sono serializzabili).
const INTRO_STORAGE_KEY = "pingup-onboarding-intro";

const loadIntro = () => {
  try {
    const raw = sessionStorage.getItem(INTRO_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const CreateBotPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  // null = entry "Raccontami la tua attività";
  // "auto" = analisi sito; "prompt" = bozza dal solo testo; "custom" = wizard manuale
  const [mode, setModeState] = useState(() =>
    sessionStorage.getItem(MODE_STORAGE_KEY)
  );
  // I File allegati viaggiano in memoria (non nel sessionStorage): o dalla
  // entry locale, o portati dalla landing pubblica via navigation state.
  const filesRef = useRef(location.state?.introFiles || []);
  // Primo bot? Decide la shell: fullscreen al primo onboarding, sidebar dopo.
  // La decisione si CONGELA alla prima risposta e non cambia più per tutta la
  // visita: se cambiasse (la creazione anticipata porta i bot 0→1 a metà
  // analisi) l'involucro fullscreen↔sidebar smonterebbe e rimonterebbe il
  // wizard, perdendo analisi e stato in corso.
  const { data: bots, isLoading: botsLoading, isError: botsError } = useBots();
  const bareShellRef = useRef(null);
  if (bareShellRef.current === null && !botsLoading && (bots || botsError)) {
    // Se la lista non arriva si resta nella shell con la barra laterale:
    // l'utente non resta bloccato su una pagina vuota e puo' andare altrove.
    bareShellRef.current = botsError ? false : (bots.length ?? 0) === 0;
  }
  // Limite di assistenti del piano gia' raggiunto: si dice all'ingresso,
  // prima dell'analisi. Anche questa decisione si congela: la creazione
  // anticipata porta gli assistenti al limite a meta' wizard, e non deve
  // bloccare il bot che si sta creando. Con un wizard gia' in corso (mode)
  // non si blocca; se i consumi non arrivano, nemmeno (decide il server).
  const consumi = useBillingUsage();
  const limiteRef = useRef(null);
  if (limiteRef.current === null && !consumi.isLoading) {
    const massimo = consumi.data?.effective?.limits?.widgets_max || 0;
    const usati = consumi.data?.usage?.widgets_count || 0;
    limiteRef.current = !mode && massimo > 0 && usati >= massimo;
  }

  const setMode = (value) => {
    if (value) {
      sessionStorage.setItem(MODE_STORAGE_KEY, value);
      // Si sta entrando nel wizard dall'entry (nuovo giro): un eventuale
      // draft rimasto da un tentativo precedente non completato (bot mai
      // confermato) farebbe ripartire il wizard dal riepilogo/config di
      // quel tentativo invece che dal prompt/sito appena inseriti.
      sessionStorage.removeItem("pingup-onboarding-draft");
    } else {
      sessionStorage.removeItem(MODE_STORAGE_KEY);
      sessionStorage.removeItem(INTRO_STORAGE_KEY);
      // Draft del wizard (incluso il bot creato): senza questa pulizia il
      // PROSSIMO onboarding ripartirebbe dal riepilogo del bot precedente.
      sessionStorage.removeItem("pingup-onboarding-draft");
    }
    setModeState(value);
  };

  const handleIntroSubmit = ({ description, websiteUrl, fileSources }) => {
    sessionStorage.setItem(
      INTRO_STORAGE_KEY,
      JSON.stringify({ description, websiteUrl })
    );
    filesRef.current = fileSources || [];
    setMode(websiteUrl ? "auto" : "prompt");
  };

  // Fine dell'onboarding: la Dashboard (/home), dall'inizio della pagina. path
  // opzionale: la pagina success porta anche a playground/widget/fonti, la
  // scelta del piano all'Abbonamento. L'assistente appena creato entra subito
  // nell'elenco (la Dashboard, senza assistenti, rimanda alla creazione) e i
  // consumi si rileggono: altrimenti mostrerebbero 0 assistenti e 0 fonti.
  const handleComplete = (bot, path) => {
    if (bot?._id) {
      queryClient.setQueryData(queryKeys.bots, (prima) =>
        Array.isArray(prima) && !prima.some((b) => b._id === bot._id) ? [...prima, bot] : prima
      );
    }
    queryClient.invalidateQueries({ queryKey: billingKeys.usage });
    setMode(null);
    navigate(path || "/home");
    window.scrollTo(0, 0);
  };

  // Il bot nasce per davvero già con la creazione anticipata (subito dopo
  // l'analisi), ben prima della Conferma finale: l'intro ha esaurito il suo
  // scopo in quel momento, non serve aspettare onComplete per farla sparire
  // (altrimenti un abbandono post-creazione-anticipata la lascerebbe in
  // sessionStorage per sempre).
  const handleIntroConsumed = () => {
    sessionStorage.removeItem(INTRO_STORAGE_KEY);
  };

  const content =
    mode === "auto" || mode === "custom" || mode === "prompt" ? (
      <CustomBotWizard
        initialMode={mode}
        intro={loadIntro()}
        introFiles={filesRef.current}
        onIntroConsumed={handleIntroConsumed}
        onComplete={handleComplete}
        onExit={() => setMode(null)}
      />
    ) : (
      <PromptIntro
        inShell={!bareShellRef.current}
        initialDraft={loadIntro()}
        initialFiles={filesRef.current}
        onSubmit={handleIntroSubmit}
        onManual={() => setMode("custom")}
      />
    );

  // Decisione shell non ancora presa (lista bot in arrivo): non montare il
  // wizard — se montasse e poi l'involucro cambiasse, il rimontaggio ne
  // perderebbe lo stato. Sfondo neutro, niente flash.
  if (bareShellRef.current === null || limiteRef.current === null) {
    return <div className="min-h-screen bg-gray-50" />;
  }
  if (limiteRef.current) {
    return (
      <MainLayout>
        <LimiteAssistenti consumi={consumi.data} />
      </MainLayout>
    );
  }
  // Primo bot: fullscreen senza sidebar né rotte — focus totale
  // sull'onboarding (e resta così per tutta la visita, anche quando la
  // creazione anticipata porta i bot a 1). Con bot già esistenti, shell
  // completa: l'utente potrebbe voler gestire i bot che ha già.
  if (bareShellRef.current) {
    // overflow-x-clip: i passi del wizard allargano lo sfondo con margini
    // negativi pensati per il padding di MainLayout, che qui non c'e'.
    return <div className="min-h-screen overflow-x-clip bg-gray-50">{content}</div>;
  }
  return <MainLayout>{content}</MainLayout>;
};

export default CreateBotPage;
